/**
 * useSecretSanta Hook
 * Manages Secret Santa event with real-time updates
 */

import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/config/firebase'

export interface SecretSantaParticipant {
  userId: string | null
  name: string
  email: string
  wishlistId: string | null
  joinedAt: any
}

export interface SecretSantaEvent {
  id: string
  creatorId: string
  title: string
  description: string
  eventDate: any
  budget: number | null
  currency: string
  allowSelfDraw: boolean
  participants: SecretSantaParticipant[]
  exclusions: Array<{
    userId: string
    excludedUserIds: string[]
  }>
  isDrawn: boolean
  drawnAt: any
  assignments: Array<{
    giverId: string
    receiverId: string
    revealedAt: any
  }>
  accessToken: string
  shareLink: string
  createdAt: any
  updatedAt: any
}

export function useSecretSanta(santaId: string | undefined) {
  const [event, setEvent] = useState<SecretSantaEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!santaId) {
      setEvent(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = onSnapshot(
      doc(db, 'secretSanta', santaId),
      (snapshot) => {
        if (snapshot.exists()) {
          setEvent({ id: snapshot.id, ...snapshot.data() } as SecretSantaEvent)
        } else {
          setEvent(null)
          setError(new Error('Secret Santa event not found'))
        }
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching Secret Santa event:', err)
        setError(err as Error)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [santaId])

  return {
    event,
    loading,
    error,
  }
}
